import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PauseWhite, PlayWhite } from '../../../assets/icons';
import {
  PRICING_TAB_VALUES,
  PROMOTION_FOCUS_VALUES,
  URL_SEARCH_PARAM_KEY,
} from '../../../constant/UrlSearchParams.constant';
import { useCompany } from '../../../hooks/useCompany';
import { useCompanyPermissions } from '../../../hooks/useCompanyPermissions';
import { useAllowedFeatures } from '../../../hooks/useFeaturePersonalization';
import { useNavigateWithSearchParam } from '../../../hooks/useNavigateWithSearchParam';
import { RoutePath } from '../../../routes';
import { ChargerOptions, useFetchChargersQuery } from '../../../services/charger.api';
import { useFetchLocationsQuery } from '../../../services/location.api';
import {
  useCreatePromotionMutation,
  useDeletePromotionMutation,
  useFetchPromotionQuery,
  usePausePromotionMutation,
  usePublishPromotionMutation,
  useResumePromotionMutation,
  useUpdatePromotionMutation,
} from '../../../services/promotion.api';
import { Location } from '../../../stores/types/location.interface';
import {
  PromotionFormErrors,
  PromotionFormState,
} from '../../../stores/types/promotionForm.interface';
import {
  ComputedPromotionStatus,
  PromoCodeResumeStrategy,
  Promotion,
} from '../../../stores/types/promotions.interface';
import { SELECTION_VALUES } from '../../../utils/paymentsReport.utils';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  MODAL_TYPES,
  Spinner,
  useGlobalModalContext,
} from '../../_ui';
import { PromoCodesSection } from '../PromoCode/PromoCodesSection.component';
import { PromotionDetailsSection } from './PromotionDetailsSection.component';
import { PromotionLanguage } from './promotionForm.constants';
import {
  buildChargerSelectionList,
  buildCreatePromotionPayload,
  buildFormStateFromPromotion,
  buildInitialFormState,
  buildUpdatePromotionPayload,
  ClearableErrorField,
  clearFieldError,
  hasFormErrors,
  validatePromotionForm,
} from './promotionForm.utils';

const NO_ERRORS: PromotionFormErrors = { labels: {} };
const CHARGER_FETCH_OPTIONS: ChargerOptions = { includeAcl: true };

export const PromotionDetailsPage = () => {
  const { t } = useTranslation();
  const { companyId, userScope } = useCompany();
  const navigate = useNavigate();
  const { allowPromotions, isFetchCompanySuccess } = useAllowedFeatures();
  const { navigateWithSearchParams } = useNavigateWithSearchParam();
  const { showModal, hideModal } = useGlobalModalContext();
  const { promotionId } = useParams<{ promotionId?: string }>();
  const { canCreatePromotion } = useCompanyPermissions(companyId);
  const [searchParams] = useSearchParams();
  // When arriving via "Add code", open straight to the promo codes section.
  const focusPromoCodes =
    searchParams.get(URL_SEARCH_PARAM_KEY.PROMOTION_FOCUS) === PROMOTION_FOCUS_VALUES.PROMO_CODES;

  const navigateBackToPromotionsTab = useCallback(() => {
    navigateWithSearchParams(`../${RoutePath.PRICING}`, {
      [URL_SEARCH_PARAM_KEY.COMPANY_ID]: companyId,
      [URL_SEARCH_PARAM_KEY.PRICING_TAB]: PRICING_TAB_VALUES.PROMOTIONS,
    });
  }, [companyId, navigateWithSearchParams]);

  const { data: chargersData, isLoading: isChargersLoading } =
    useFetchChargersQuery(CHARGER_FETCH_OPTIONS);

  const {
    data: existingPromotion,
    isLoading: isExistingPromotionLoading,
    isError: isPromotionError,
  } = useFetchPromotionQuery({ promotionId: promotionId ?? '' }, { skip: !promotionId });

  const { locations, isLocationsLoading } = useFetchLocationsQuery(
    { scope: userScope!, include: SELECTION_VALUES.ALL },
    {
      skip: !userScope,
      selectFromResult: ({
        data,
        isLoading,
      }: {
        data?: { entities: Location[] };
        isLoading: boolean;
      }) => ({
        locations: data?.entities ?? [],
        isLocationsLoading: isLoading,
      }),
    },
  );

  const [triggerCreatePromotion, createState] = useCreatePromotionMutation();
  const [triggerUpdatePromotion, updateState] = useUpdatePromotionMutation();
  const [triggerPublishPromotion, publishState] = usePublishPromotionMutation();
  const [triggerPausePromotion, pauseState] = usePausePromotionMutation();
  const [triggerResumePromotion, resumeState] = useResumePromotionMutation();
  const [triggerDeletePromotion] = useDeletePromotionMutation();

  const locationNameToId = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((loc) => map.set(loc.name, loc.id));
    return map;
  }, [locations]);

  const chargerList = useMemo(() => {
    if (locationNameToId.size === 0) return [];
    return buildChargerSelectionList(chargersData?.entities ?? [], t, locationNameToId);
  }, [chargersData, t, locationNameToId]);

  useEffect(() => {
    if (chargerList.length === 0) return;

    setFormState((prev) =>
      prev.chargers.length === 0 ? { ...prev, chargers: chargerList } : prev,
    );
  }, [chargerList]);

  const [formState, setFormState] = useState<PromotionFormState>(() =>
    buildInitialFormState(chargerList),
  );
  const [errors, setErrors] = useState<PromotionFormErrors>(NO_ERRORS);
  const [isPromotionDetailsExpanded, setIsPromotionDetailsExpanded] = useState(!focusPromoCodes);
  const [savedPromotion, setSavedPromotion] = useState<Promotion | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasPromoCodeErrors, setHasPromoCodeErrors] = useState(false);

  // Populate chargers once the list is loaded; never wipe the user's selections on later updates.
  useEffect(() => {
    if (chargerList.length === 0 || locationNameToId.size === 0) return;

    setFormState((prev) =>
      prev.chargers.length === 0 ? { ...prev, chargers: chargerList } : prev,
    );
  }, [chargerList, locationNameToId]);

  // When viewing an existing promotion, hydrate savedPromotion AND the form fields
  // (so the section header + read-only details show the actual promotion data).
  useEffect(() => {
    if (!existingPromotion || chargerList.length === 0) return;
    setSavedPromotion(existingPromotion);
    setFormState(buildFormStateFromPromotion(existingPromotion, chargerList));
  }, [existingPromotion?.promotionId, chargerList.length]);

  // Redirect to 404 if promotion not found or promotions feature not allowed
  useEffect(() => {
    if (isPromotionError) navigate(RoutePath.NOT_FOUND, { replace: true });
  }, [isPromotionError, navigate]);

  // Redirect to company overview page if promotions feature not allowed
  useEffect(() => {
    if (!isFetchCompanySuccess) return;
    if (!allowPromotions)
      navigateWithSearchParams(`../${RoutePath.OVERVIEW}`, {
        [URL_SEARCH_PARAM_KEY.COMPANY_ID]: companyId,
      });
  }, [allowPromotions, isFetchCompanySuccess, companyId, navigateWithSearchParams]);

  const handleChange = useCallback((updater: (prev: PromotionFormState) => PromotionFormState) => {
    setFormState(updater);
  }, []);

  const handleClearError = useCallback((field: ClearableErrorField) => {
    setErrors((prev) => clearFieldError(prev, field));
  }, []);

  const handleShowSecondaryLanguage = useCallback((lang: PromotionLanguage) => {
    setFormState((prev) =>
      prev.visibleOptionalLanguages.includes(lang)
        ? prev
        : { ...prev, visibleOptionalLanguages: [...prev.visibleOptionalLanguages, lang] },
    );
  }, []);

  const handleHideSecondaryLanguage = useCallback((lang: PromotionLanguage) => {
    setFormState((prev) => ({
      ...prev,
      visibleOptionalLanguages: prev.visibleOptionalLanguages.filter((l) => l !== lang),
      labels: { ...prev.labels, [lang]: '' },
    }));
  }, []);

  const handleEditPromotion = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const savedPromotionValidationErrors = useMemo(
    () => (savedPromotion?.status === 'DRAFT' ? validatePromotionForm(formState, t) : NO_ERRORS),
    [savedPromotion?.status, formState, t],
  );

  const handleSavePromotionDetails = useCallback(async () => {
    if (!companyId) return;

    const validationErrors = validatePromotionForm(formState, t);
    if (hasFormErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }
    setErrors(NO_ERRORS);

    try {
      let result: Promotion;
      if (savedPromotion?.promotionId) {
        const updatePayload = buildUpdatePromotionPayload(formState);
        result = await triggerUpdatePromotion({
          promotionId: savedPromotion.promotionId,
          ...updatePayload,
        }).unwrap();
      } else {
        const createPayload = buildCreatePromotionPayload(formState, companyId);
        result = await triggerCreatePromotion(createPayload).unwrap();
      }
      setSavedPromotion(result);
      setIsPromotionDetailsExpanded(true);
      setIsEditMode(false);
    } catch {
      // Toast handled by base.api error pipeline.
    }
  }, [companyId, formState, t, triggerCreatePromotion, triggerUpdatePromotion, savedPromotion]);

  const handleDeletePromotion = useCallback(() => {
    if (!savedPromotion) return;
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('promotion_delete_modal_title'),
      width: '400px',
      height: 'max-content',
      message: (
        <Trans i18nKey='promotion_delete_modal_message' values={{ name: savedPromotion.name }} />
      ),
      iconColor: ColorType.NEGATIVE,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          dataTestId: 'cancelDeletePromotion',
        },
        {
          label: t('confirm_delete_promotion'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          dataTestId: 'confirmDeletePromotion',
          onClick: async () => {
            try {
              await triggerDeletePromotion({ promotionId: savedPromotion.promotionId }).unwrap();
              hideModal();
              navigateBackToPromotionsTab();
            } catch {
              hideModal();
            }
          },
        },
      ],
    });
  }, [
    hideModal,
    navigateBackToPromotionsTab,
    savedPromotion,
    showModal,
    t,
    triggerDeletePromotion,
  ]);

  const handleArchivePromotion = useCallback(() => {
    if (!savedPromotion) return;
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('promotion_archive_modal_title'),
      width: '400px',
      height: 'max-content',
      message: (
        <Trans i18nKey='promotion_archive_modal_message' values={{ name: savedPromotion.name }} />
      ),
      iconColor: ColorType.NEGATIVE,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          dataTestId: 'cancelDeletePromotion',
        },
        {
          label: t('confirm_archive_promotion'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          dataTestId: 'confirmArchivePromotion',
          onClick: async () => {
            try {
              await triggerDeletePromotion({ promotionId: savedPromotion.promotionId }).unwrap();
              hideModal();
              navigateBackToPromotionsTab();
            } catch {
              hideModal();
            }
          },
        },
      ],
    });
  }, [
    hideModal,
    navigateBackToPromotionsTab,
    savedPromotion,
    showModal,
    t,
    triggerDeletePromotion,
  ]);

  const handlePublishPromotion = useCallback(() => {
    if (!savedPromotion) return;

    const validationErrors = validatePromotionForm(formState, t);
    if (hasFormErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('promotion_publish_modal_title'),
      width: '400px',
      height: 'max-content',
      message: (
        <Trans
          i18nKey={
            savedPromotion.promoCodeCount === 0
              ? 'promotion_publish_modal_message'
              : 'promotion_publish_with_code_modal_message'
          }
          values={{ name: savedPromotion.name }}
        />
      ),
      iconColor: ColorType.GREY5,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          dataTestId: 'cancelPublishPromotion',
        },
        {
          label: t('confirm_publish_promotion'),
          type: ButtonType.PRIMARY,
          size: ButtonSize.SMALL,
          dataTestId: 'confirmPublishPromotion',
          onClick: async () => {
            try {
              const result = await triggerPublishPromotion({
                promotionId: savedPromotion.promotionId,
                cascadeToPromoCodes: true,
              }).unwrap();
              setSavedPromotion(result);
              hideModal();
            } catch {
              hideModal();
            }
          },
        },
      ],
    });
  }, [hideModal, savedPromotion, showModal, t, triggerPublishPromotion]);

  const handlePausePromotion = useCallback(() => {
    if (!savedPromotion) return;
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('promotion_pause_modal_title'),
      width: '400px',
      height: 'max-content',
      message: (
        <Trans i18nKey='promotion_pause_modal_message' values={{ name: savedPromotion.name }} />
      ),
      iconColor: ColorType.GREY5,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          dataTestId: 'cancelpausePromotion',
        },
        {
          label: t('confirm_pause_promotion'),
          type: ButtonType.PRIMARY,
          size: ButtonSize.SMALL,
          dataTestId: 'confirmpausePromotion',
          onClick: async () => {
            try {
              const result = await triggerPausePromotion({
                promotionId: savedPromotion.promotionId,
              }).unwrap();
              setSavedPromotion(result);
              hideModal();
            } catch {
              hideModal();
            }
          },
        },
      ],
    });
  }, [hideModal, savedPromotion, showModal, t, triggerPausePromotion]);

  const handleResumePromotion = useCallback(() => {
    if (!savedPromotion) return;
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('promotion_resume_modal_title'),
      width: '400px',
      height: 'max-content',
      message: (
        <Trans i18nKey='promotion_resume_modal_message' values={{ name: savedPromotion.name }} />
      ),
      iconColor: ColorType.GREY5,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          dataTestId: 'cancelresumePromotion',
        },
        {
          label: t('confirm_resume_promotion'),
          type: ButtonType.PRIMARY,
          size: ButtonSize.SMALL,
          dataTestId: 'confirmresumePromotion',
          onClick: async () => {
            try {
              const result = await triggerResumePromotion({
                promotionId: savedPromotion.promotionId,
                promoCodeResumeStrategy: PromoCodeResumeStrategy.SYSTEM_PAUSED_ONLY,
              }).unwrap();
              setSavedPromotion(result);
              hideModal();
            } catch {
              hideModal();
            }
          },
        },
      ],
    });
  }, [hideModal, savedPromotion, showModal, t, triggerResumePromotion]);

  // Also wait for `userScope` to be defined — when it's undefined the locations
  // query is skipped (and returns isLoading=false), which would let the form
  // initialize with an empty name→id map and bake the names into formState.chargers.
  if (!userScope || isChargersLoading || isLocationsLoading || isExistingPromotionLoading) {
    return (
      <div className='flex items-center justify-center w-full py-12'>
        <Spinner height='h-9' width='w-9' />
      </div>
    );
  }

  return (
    <Card>
      <div className='flex flex-col gap-5'>
        <PromotionDetailsSection
          state={formState}
          errors={{ ...savedPromotionValidationErrors, ...errors }}
          isExpanded={isPromotionDetailsExpanded}
          isSaving={createState.isLoading || updateState.isLoading}
          isReadOnly={
            !!savedPromotion &&
            (!isEditMode || savedPromotion.status !== ComputedPromotionStatus.DRAFT)
          }
          status={savedPromotion?.status}
          onToggleExpand={() => setIsPromotionDetailsExpanded((prev) => !prev)}
          onChange={handleChange}
          onClearError={handleClearError}
          onSave={handleSavePromotionDetails}
          onShowSecondaryLanguage={handleShowSecondaryLanguage}
          onHideSecondaryLanguage={handleHideSecondaryLanguage}
        />

        {savedPromotion && (
          <div className='flex flex-row justify-end gap-3'>
            {savedPromotion.status === ComputedPromotionStatus.DRAFT && (
              <Button
                label={t('delete_promotion')}
                type={ButtonType.DESTRUCTIVE}
                size={ButtonSize.SMALL}
                onClick={handleDeletePromotion}
                dataTestId='deletePromotion'
              />
            )}
            {(savedPromotion.status === ComputedPromotionStatus.PAUSED ||
              savedPromotion.status === ComputedPromotionStatus.EXPIRED) && (
              <Button
                label={t('archive_promotion')}
                type={ButtonType.DESTRUCTIVE}
                size={ButtonSize.SMALL}
                onClick={handleArchivePromotion}
                dataTestId='archivePromotion'
              />
            )}
            {savedPromotion.status === ComputedPromotionStatus.DRAFT && !isEditMode && (
              <Button
                label={t('edit_promotion_details')}
                type={ButtonType.PRIMARY}
                size={ButtonSize.SMALL}
                onClick={handleEditPromotion}
                dataTestId='editPromotion'
              />
            )}
          </div>
        )}

        <PromoCodesSection
          promotion={savedPromotion}
          companyId={companyId ?? ''}
          canManage={canCreatePromotion}
          autoAddCode={focusPromoCodes}
          onValidationChange={setHasPromoCodeErrors}
        />

        <div className='flex flex-row justify-end gap-3 pt-2'>
          <Button
            label={t('back')}
            type={ButtonType.TERTIARY}
            size={ButtonSize.SMALL}
            onClick={navigateBackToPromotionsTab}
            dataTestId='backFromPromotion'
          />
          {!isEditMode && savedPromotion?.status === ComputedPromotionStatus.DRAFT && (
            <Button
              label={t('publish_promotion')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.SMALL}
              disabled={hasFormErrors(savedPromotionValidationErrors) || hasPromoCodeErrors}
              onClick={handlePublishPromotion}
              isLoading={publishState.isLoading}
              dataTestId='publishPromotion'
            />
          )}
          {(savedPromotion?.status === ComputedPromotionStatus.ACTIVE ||
            savedPromotion?.status === ComputedPromotionStatus.SCHEDULED) && (
            <Button
              icon={PauseWhite}
              label={t('pause_promotion')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={handlePausePromotion}
              isLoading={pauseState.isLoading}
              dataTestId='pausePromotion'
            />
          )}
          {savedPromotion?.status === ComputedPromotionStatus.PAUSED && (
            <Button
              icon={PlayWhite}
              label={t('resume_promotion')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={handleResumePromotion}
              isLoading={resumeState.isLoading}
              dataTestId='resumePromotion'
            />
          )}
        </div>
      </div>
    </Card>
  );
};

PromotionDetailsPage.displayName = 'PromotionDetailsPage';
